import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Center,
  ScrollView,
  Stack,
  Input,
  Pressable as NBPressable,
} from "native-base";
import {
  Animated,
  Dimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { GlobalStyles } from "../../constants/styles";
import BusinessReservation from "../BusinessServices/BusinessReservation";

const AGENT_BASE = "https://reservation-agent.townticks.com";

const cardWidthProp = ["92%", "85%", 650];

const figmaShadow =
  Platform.OS === "web"
    ? { boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.25)" }
    : {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 4,
      };

const _resolveInitialTab = (actionType) => {
  switch (actionType) {
    case "OPEN_CHECK_RESERVATION":
      return "check";
    case "OPEN_RESERVATION_ANALYTICS":
    case "OPEN_RESERVATION_STATS":
      return "analytics";
    case "OPEN_RESERVATION_SETTINGS":
      return "settings";
    case "OPEN_MAKE_RESERVATION":
    case "OPEN_CREATE_RESERVATION":
      return "makeReservation";
    case "OPEN_RESERVATION_APP":
    default:
      return "menu";
  }
};

const _resolvePopupTitle = (actionType) => {
  switch (actionType) {
    case "OPEN_CHECK_RESERVATION":
      return "Check Reservation";
    case "OPEN_RESERVATION_ANALYTICS":
    case "OPEN_RESERVATION_STATS":
      return "Reservation Analytics";
    case "OPEN_RESERVATION_SETTINGS":
      return "Reservation Settings";
    case "OPEN_MAKE_RESERVATION":
    case "OPEN_CREATE_RESERVATION":
      return "Make Reservation";
    default:
      return "Reservation App";
  }
};

const GradientSendButton = ({ onPress, disabled }) => (
  <Box
    borderRadius={10}
    overflow="hidden"
    borderWidth={1}
    borderColor={GlobalStyles.colors.black}
    opacity={disabled ? 0.38 : 1}
    style={{ width: 44, height: 44, flexShrink: 0 }}
  >
    <LinearGradient
      colors={[
        GlobalStyles.colors.gradientBlueStart ?? "#000561",
        GlobalStyles.colors.gradientBlueEnd ?? "#000AC7",
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{
        width: 44,
        height: 44,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={{
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            color: GlobalStyles.colors.mustard || "#F5A400",
            fontSize: 18,
            fontFamily: GlobalStyles.text?.fontFamily,
          }}
        >
          ➤
        </Text>
      </Pressable>
    </LinearGradient>
  </Box>
);

const QuickReplyChip = ({ label, onPress, disabled }) => (
  <Box
    borderRadius={20}
    overflow="hidden"
    borderWidth={1}
    borderColor={GlobalStyles.colors.black}
    opacity={disabled ? 0.45 : 1}
    mr={2}
    mb={1}
  >
    <LinearGradient
      colors={[
        GlobalStyles.colors.gradientBlueStart ?? "#000561",
        GlobalStyles.colors.gradientBlueEnd ?? "#000AC7",
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={{ paddingHorizontal: 14, paddingVertical: 8 }}
    >
      <Pressable
        onPress={() => !disabled && onPress(label)}
        disabled={disabled}
      >
        <Text
          style={{
            color: GlobalStyles.colors.mustard || "#F5A400",
            fontWeight: "600",
            fontSize: 13,
            fontFamily: GlobalStyles.text?.fontFamily,
          }}
        >
          {label}
        </Text>
      </Pressable>
    </LinearGradient>
  </Box>
);

const TypingDot = ({ delay }) => {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(anim, { toValue: 1, duration: 350, useNativeDriver: true }),
        Animated.timing(anim, { toValue: 0, duration: 350, useNativeDriver: true }),
      ])
    ).start();
  }, [anim, delay]);

  return (
    <Animated.View
      style={{
        width: 7,
        height: 7,
        borderRadius: 4,
        backgroundColor: GlobalStyles.colors.gradientBlueEnd || "#000AC7",
        marginHorizontal: 2,
        opacity: anim,
        transform: [
          { translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [0, -4] }) },
        ],
      }}
    />
  );
};

const renderMd = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <Text key={i} fontWeight="700" fontFamily={GlobalStyles.text?.fontFamily}>
          {part.slice(2, -2)}
        </Text>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <Text
          key={i}
          fontFamily="monospace"
          fontSize="13px"
          bg="rgba(0,0,0,0.06)"
          px={1}
          borderRadius={4}
        >
          {part.slice(1, -1)}
        </Text>
      );
    }
    return (
      <Text key={i} fontFamily={GlobalStyles.text?.fontFamily}>
        {part}
      </Text>
    );
  });
};

const MessageBubble = ({ msg, onQuickReply, isLastAgent, loading }) => {
  const isUser = msg.role === "user";
  const showQR = !isUser && isLastAgent && msg.quickReplies?.length > 0;

  return (
    <VStack mb={showQR ? 1 : 3}>
      <HStack
        justifyContent={isUser ? "flex-end" : "flex-start"}
        alignItems="flex-end"
        space={2}
      >
        {!isUser && (
          <Box
            w={7}
            h={7}
            borderRadius={14}
            bg={GlobalStyles.colors.blue || "#002061"}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Text
              fontSize="9px"
              fontWeight="800"
              color={GlobalStyles.colors.mustard || "#F5A400"}
              fontFamily={GlobalStyles.text?.fontFamily}
            >
              AI
            </Text>
          </Box>
        )}

        <Box
          maxW="75%"
          bg={
            isUser
              ? GlobalStyles.colors.blue || "#002061"
              : GlobalStyles.colors.white
          }
          borderRadius={12}
          px={4}
          py={2.5}
          borderWidth={1}
          borderColor={isUser ? "transparent" : "#E2E5EC"}
          style={isUser ? {} : figmaShadow}
        >
          <Text
            fontSize="14px"
            lineHeight="21px"
            color={isUser ? GlobalStyles.colors.white : GlobalStyles.colors.black}
            fontFamily={GlobalStyles.text?.fontFamily}
          >
            {renderMd(msg.content)}
          </Text>
        </Box>
      </HStack>

      {showQR && (
        <Box ml={9} mt={2}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <HStack flexWrap="nowrap" pb={1}>
              {msg.quickReplies.map((label, i) => (
                <QuickReplyChip
                  key={i}
                  label={label}
                  onPress={onQuickReply}
                  disabled={loading}
                />
              ))}
            </HStack>
          </ScrollView>
        </Box>
      )}
    </VStack>
  );
};

const TypingIndicator = () => (
  <HStack alignItems="flex-end" space={2} mb={3}>
    <Box
      w={7}
      h={7}
      borderRadius={14}
      bg={GlobalStyles.colors.blue || "#002061"}
      alignItems="center"
      justifyContent="center"
    >
      <Text
        fontSize="9px"
        fontWeight="800"
        color={GlobalStyles.colors.mustard || "#F5A400"}
      >
        AI
      </Text>
    </Box>
    <Box
      bg={GlobalStyles.colors.white}
      borderRadius={12}
      px={4}
      py={3}
      borderWidth={1}
      borderColor="#E2E5EC"
      style={figmaShadow}
    >
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <TypingDot delay={0} />
        <TypingDot delay={150} />
        <TypingDot delay={300} />
      </View>
    </Box>
  </HStack>
);

const { height: SCREEN_H, width: SCREEN_W } = Dimensions.get("window");

const ReservationAppPopup = ({ visible, onClose, action }) => (
  <Modal
    visible={visible}
    transparent
    animationType="fade"
    onRequestClose={onClose}
    statusBarTranslucent
  >
    <View
      style={{
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.82)",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Pressable
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        onPress={onClose}
      />

      <View
        style={{
          width: Math.min(SCREEN_W * 0.92, 860),
          maxHeight: SCREEN_H * 0.88,
          backgroundColor: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          elevation: 24,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.45,
          shadowRadius: 20,
        }}
      >
        <LinearGradient
          colors={[
            GlobalStyles.colors.gradientBlueStart ?? "#000561",
            GlobalStyles.colors.gradientBlueEnd ?? "#000AC7",
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{
            height: 56,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingHorizontal: 16,
          }}
        >
          <Pressable
            onPress={onClose}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              backgroundColor: "rgba(255,255,255,0.12)",
              borderRadius: 20,
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderWidth: 1.5,
              borderColor: GlobalStyles.colors.mustard || "#F5A400",
              minWidth: 120,
            }}
          >
            <Text
              style={{
                color: GlobalStyles.colors.mustard || "#F5A400",
                fontSize: 16,
                fontWeight: "700",
              }}
            >
              ←
            </Text>
            <Text
              style={{
                color: GlobalStyles.colors.mustard || "#F5A400",
                fontWeight: "700",
                fontSize: 13,
                fontFamily: GlobalStyles.text?.fontFamily,
              }}
            >
              Back to Chat
            </Text>
          </Pressable>

          <Text
            style={{
              color: "#fff",
              fontSize: 16,
              fontWeight: "700",
              flex: 1,
              textAlign: "center",
              fontFamily: GlobalStyles.text?.fontFamily,
            }}
          >
            {_resolvePopupTitle(action?.type)}
          </Text>

          <View style={{ minWidth: 120 }} />
        </LinearGradient>

        <View style={{ flex: 1 }}>
          <BusinessReservation
            initialTab={_resolveInitialTab(action?.type)}
            onBack={onClose}
            showBackButton={false}
          />
        </View>
      </View>
    </View>
  </Modal>
);

const ReservationAgentWidget = ({ onBack, serviceName, userId, userToken }) => {
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [interrupted, setInterrupted] = useState(false);

  const [popupOpen, setPopupOpen]     = useState(false);
  const [popupAction, setPopupAction] = useState(null);

  const scrollRef = useRef(null);

  useEffect(() => { fetchWelcome(); }, []);

  const scrollToBottom = (delay = 100) =>
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), delay);

  const handleUiAction = useCallback((action) => {
    if (!action) return;
    setPopupAction(action);
    setPopupOpen(true);
  }, []);

  const appendMsg = (role, content, quickReplies = []) =>
    setMessages((prev) => [...prev, { role, content, quickReplies }]);

  const buildHeaders = () => ({
    "Content-Type": "application/json",
    ...(userToken ? { Authorization: `bearer ${userToken}` } : {}),
  });

  const fetchWelcome = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${AGENT_BASE}/chat`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ user_id: String(userId || "1"), message: "hello" }),
      });
      const data = await res.json();
      setMessages([{
        role: "assistant",
        content: data.reply || "Hi! How can I help?",
        quickReplies: data.quick_replies || [
          "Make a reservation",
          "Check a reservation",
          "Show reservation stats",
          "Reservation settings",
          "Open Reservation App",
        ],
      }]);
    } catch {
      setMessages([{
        role: "assistant",
        content:
          "Hi! 👋 I am your **Reservation Agent**.\n\n" +
          "I can help you:\n" +
          "• 📅 Make a reservation\n" +
          "• 🔍 Check an existing reservation\n" +
          "• 📊 Check reservation stats\n" +
          "• ⚙️ Manage reservation settings\n" +
          "• 📱 Jump to the Reservation App anytime\n\n" +
          "What would you like to do?",
        quickReplies: [
          "Make a reservation",
          "Check a reservation",
          "Show reservation stats",
          "Reservation settings",
          "Open Reservation App",
        ],
      }]);
    } finally {
      setLoading(false);
      scrollToBottom(150);
    }
  }, [userId, userToken]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setMessages((prev) => prev.map((m) => ({ ...m, quickReplies: [] })));

    appendMsg("user", trimmed);
    setInput("");
    setLoading(true);
    scrollToBottom(80);

    try {
      const endpoint = interrupted ? "/resume" : "/chat";
      const body = interrupted
        ? { user_id: String(userId || "1"), answer: trimmed }
        : { user_id: String(userId || "1"), message: trimmed };

      const res = await fetch(`${AGENT_BASE}${endpoint}`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();

      appendMsg("assistant", data.reply || "Done.", data.quick_replies || []);
      setInterrupted(data.interrupted || false);

      if (data.ui_action) handleUiAction(data.ui_action);
    } catch {
      appendMsg("assistant", "❌ Sorry, something went wrong. Please try again.", ["Try again"]);
    } finally {
      setLoading(false);
      scrollToBottom(150);
    }
  }, [loading, interrupted, input, userId, userToken, handleUiAction]);

  const send = useCallback(() => sendMessage(input), [input, sendMessage]);

  const handleQuickReply = useCallback(
    (label) => {
      setMessages((prev) => prev.map((m) => ({ ...m, quickReplies: [] })));
      sendMessage(label);
    },
    [sendMessage]
  );

  const lastAgentIdx = messages.reduceRight(
    (found, msg, i) => (found === -1 && msg.role === "assistant" ? i : found),
    -1
  );

  return (
    <>
      <ReservationAppPopup
        visible={popupOpen}
        onClose={() => setPopupOpen(false)}
        action={popupAction}
      />

      <ScrollView flex={1} bg="muted.100" keyboardShouldPersistTaps="handled">
        <Center>
          <Stack
            mt={4}
            w={cardWidthProp}
            alignItems="center"
            justifyContent="center"
          >
            {onBack && (
              <HStack w="100%" mb={2} px={[2, 4, 4]}>
                <NBPressable onPress={onBack}>
                  <HStack alignItems="center" space={2}>
                    <Text
                      color={GlobalStyles.colors.blue}
                      fontWeight="500"
                      fontSize={[13, 14, 14]}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      ← Back to Services
                    </Text>
                  </HStack>
                </NBPressable>
              </HStack>
            )}

            <Box
              w="100%"
              bg={GlobalStyles.colors.white}
              borderRadius={12}
              px={[4, 5, 6]}
              py={[3, 4, 4]}
              mb={4}
              style={figmaShadow}
            >
              <VStack space={3}>

                <HStack justifyContent="space-between" alignItems="center">
                  <Text
                    fontSize={["16px", "18px", "20px"]}
                    fontWeight="500"
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Reservation Agent
                  </Text>
                  <Box px={3} py={1} borderRadius={20} bg="rgba(0,32,97,0.08)">
                    <Text
                      fontSize="12px"
                      color={GlobalStyles.colors.blue}
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontWeight="500"
                    >
                      {serviceName || "AI Assistant"}
                    </Text>
                  </Box>
                </HStack>

                {interrupted && (
                  <Box
                    bg="#FFF3CD"
                    px={3}
                    py={2}
                    borderRadius={8}
                    borderWidth={1}
                    borderColor="#F5A400"
                  >
                    <Text
                      fontSize="12px"
                      color="#856404"
                      textAlign="center"
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      ⏸ Waiting for your reply to continue…
                    </Text>
                  </Box>
                )}

                <Box
                  borderRadius={10}
                  borderWidth={1}
                  borderColor="#E2E5EC"
                  bg="#F8F9FB"
                  overflow="hidden"
                  style={{ height: 380 }}
                >
                  <ScrollView
                    ref={scrollRef}
                    flex={1}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{ padding: 14 }}
                    keyboardShouldPersistTaps="handled"
                  >
                    {messages.map((msg, i) => (
                      <MessageBubble
                        key={i}
                        msg={msg}
                        onQuickReply={handleQuickReply}
                        isLastAgent={i === lastAgentIdx}
                        loading={loading}
                      />
                    ))}
                    {loading && <TypingIndicator />}
                  </ScrollView>
                </Box>

                <KeyboardAvoidingView
                  behavior={Platform.OS === "ios" ? "padding" : "height"}
                  keyboardVerticalOffset={0}
                >
                  <HStack space={2} alignItems="flex-end">
                    <Box flex={1}>
                      <Input
                        value={input}
                        onChangeText={setInput}
                        placeholder={interrupted ? "Reply to continue…" : "Ask me anything…"}
                        placeholderTextColor="#9096A8"
                        returnKeyType="send"
                        onSubmitEditing={send}
                        multiline
                        maxLength={500}
                        borderRadius={10}
                        borderWidth={1}
                        borderColor="#E2E5EC"
                        bg="#F2F4F8"
                        px={4}
                        py={Platform.OS === "ios" ? 2.5 : 2}
                        fontSize="14px"
                        color={GlobalStyles.colors.black}
                        maxH={80}
                        fontFamily={GlobalStyles.text.fontFamily}
                        _focus={{
                          borderColor: GlobalStyles.colors.gradientBlueEnd || "#000AC7",
                        }}
                      />
                    </Box>
                    <GradientSendButton
                      onPress={send}
                      disabled={!input.trim() || loading}
                    />
                  </HStack>
                </KeyboardAvoidingView>

              </VStack>
            </Box>
          </Stack>
        </Center>
      </ScrollView>
    </>
  );
};

export default ReservationAgentWidget;